import { useState } from "react";
import { useApi } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { Search, LifeBuoy, PlayCircle, ChevronLeft, ChevronRight, CheckCircle2, Rocket, ShieldAlert, Wallet, ScrollText, Plug, TrendingUp, Sparkles, Clock } from "lucide-react";

const TOURS = [
  {
    id: "onboarding", title: "Your first 10 minutes", audience: ["Tenant Admin", "New admin"], icon: Rocket, color: "#1A3A5C",
    intro: "A quick orientation to Navitra OS — the desktop, the apps, and where the value lives.",
    steps: [
      { title: "This is a real desktop OS", detail: "Everything runs as draggable, resizable windows inside /os. Use the dock at the bottom to launch pinned apps, or the launcher (grid icon) for the full catalogue. Right-click the desktop for wallpapers and shortcuts." },
      { title: "Start at the Cockpit", detail: "Open Cockpit for the command view: governed AI spend, blocked events, active users and savings — all on live seeded data. Every chart has a 'Deep dive & AI guidance' link that explains what it means and how to improve it." },
      { title: "Personalise your workspace", detail: "Click the palette icon (top bar) for the Appearance panel — 11 themes across Professional, Editorial and Vibrant groups, plus layout (Navitra / Windows / macOS), language and a digital or analog clock." },
      { title: "Know your access", detail: "Your role decides which apps appear and what you can reveal. Open Profile & preferences from your avatar menu to manage your identity, MFA and desktop density." },
    ],
  },
  {
    id: "investigate", title: "Investigate a blocked action", audience: ["Security Admin", "SecOps"], icon: ShieldAlert, color: "#DC2626",
    intro: "Trace exactly what an employee tried to do, why it was blocked, and the sensitive data involved.",
    steps: [
      { title: "Open Workforce", detail: "Launch the Workforce app. The employee table shows interactions, spend, blocked actions and sensitive events per person. Sort by blocked or use the search and department filter to narrow down." },
      { title: "Deep dive on the employee", detail: "Click any row to open the deep-dive drawer: spend trend, vendor mix, top use cases and a sensitive-data exposure summary." },
      { title: "Read the activity with privacy tiers", detail: "In Recent activity, switch the privacy toggle between Metadata, Redacted and Full content. Only authorised roles can reveal redacted snippets or full prompt text — everyone else sees metadata only." },
      { title: "Export evidence", detail: "Use Export PDF report or Export CSV for an audit-ready record. Exports contain metadata only — prompt content stays governed by privacy tiers." },
    ],
  },
  {
    id: "budget-alert", title: "Set a budget & usage alert", audience: ["FinOps Admin"], icon: Wallet, color: "#B8962E",
    intro: "Put guardrails on individual and departmental AI spend, and get alerted quietly when they're crossed.",
    steps: [
      { title: "Allocate a department budget", detail: "In Workforce → Departments & budgets, set a monthly budget for any department. Over-budget departments are flagged automatically." },
      { title: "Set a per-employee budget", detail: "Open an employee's deep dive and set their monthly budget under Budget allocation. Over-budget people are badged in the list." },
      { title: "Create a quiet alert rule", detail: "In the deep dive, add a Usage alert rule (e.g. spend > $400, or blocked actions ≥ 5). These are evaluated for admins only and deliberately never appear in the notification bell." },
      { title: "Monitor the Alerts tab", detail: "Workforce → Usage alerts shows every rule, its current value and whether it's firing — a calm, standalone monitoring surface." },
    ],
  },
  {
    id: "policy", title: "Author & simulate a policy", audience: ["Policy Author"], icon: ScrollText, color: "#7C3AED",
    intro: "Draft a governance policy and test its decision before it goes live.",
    steps: [
      { title: "Open Policies", detail: "Launch the Policies app and click 'New policy' to draft a rule from a template (PII redaction, source-code protection, budget guard, and more)." },
      { title: "Define the intent", detail: "Give the policy a clear name and scope. Drafts are safe — nothing is enforced until you promote it." },
      { title: "Simulate the decision", detail: "Run a simulation against a sample AI event to see the deterministic outcome: allow, coach, redact or block — with the reason." },
      { title: "Promote when confident", detail: "Once the simulation looks right, promote the draft. The Cockpit and Risk views immediately reflect enforcement." },
    ],
  },
  {
    id: "integration", title: "Connect an integration", audience: ["Integration Admin"], icon: Plug, color: "#0EA5E9",
    intro: "Add a connector with the exact parameters it needs and verify the connection.",
    steps: [
      { title: "Open Integration Hub", detail: "Launch Integration Hub to see the connector catalogue (Entra ID, Anthropic, OpenAI, gateways and more) with live health status." },
      { title: "Configure the parameters", detail: "Open a connector's wizard. Navitra shows the exact required fields — tenant ID, client ID, secrets — with secret fields masked." },
      { title: "Test the connection", detail: "Run the Test step. Navitra validates the parameters and returns a specific success or failure message so you know precisely what to fix." },
      { title: "Go live", detail: "Save the configuration to bring the connector online. Its guide is always one click away in Help & Guides." },
    ],
  },
  {
    id: "impact", title: "Export an executive impact report", audience: ["Exec Viewer", "Tenant Admin"], icon: TrendingUp, color: "#0E7C66",
    intro: "Turn governance activity into a management-ready proof of value.",
    steps: [
      { title: "Open Impact Analytics", detail: "Launch Impact Analytics and choose a period — Week, Month, Year or a custom start/end range on the calendar." },
      { title: "Read the impact story", detail: "Review governance, compliance, monitoring, budgeting and savings KPIs. Each chart includes narration and AI guidance." },
      { title: "Export PDF or PPT", detail: "Generate a PDF report or a PowerPoint deck. Both include charts and a plain-language narrative framed around ROI and effectiveness — ready for leadership." },
    ],
  },
];

function WalkthroughDialog({ tour, onOpenChange }) {
  const [i, setI] = useState(0);
  if (!tour) return null;
  const last = i === tour.steps.length - 1;
  const Icon = tour.icon;
  const pct = ((i + 1) / tour.steps.length) * 100;
  return (
    <Dialog open={!!tour} onOpenChange={(o) => { if (!o) { setI(0); onOpenChange(false); } }}>
      <DialogContent className="max-w-lg" data-testid="tour-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <span className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: tour.color }}><Icon className="h-3.5 w-3.5 text-white" /></span>
            {tour.title}
          </DialogTitle>
          <DialogDescription>Step {i + 1} of {tour.steps.length}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Progress value={pct} className="h-1.5" data-testid="tour-progress" />
          <div className="rounded-lg border bg-muted/30 p-4 min-h-[120px]">
            <p className="font-medium text-sm flex items-center gap-2"><span className="h-5 w-5 rounded-full bg-navy text-white text-[11px] flex items-center justify-center">{i + 1}</span>{tour.steps[i].title}</p>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="tour-step-detail">{tour.steps[i].detail}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {tour.steps.map((_, idx) => <span key={idx} className={`h-1.5 flex-1 rounded-full transition-colors ${idx <= i ? "bg-navy" : "bg-muted"}`} />)}
          </div>
        </div>
        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={() => setI((v) => Math.max(0, v - 1))} disabled={i === 0} data-testid="tour-back"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
          {last
            ? <Button onClick={() => { setI(0); onOpenChange(false); }} data-testid="tour-finish"><CheckCircle2 className="h-4 w-4 mr-1.5" />Finish</Button>
            : <Button onClick={() => setI((v) => Math.min(tour.steps.length - 1, v + 1))} data-testid="tour-next">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HelpGuides() {
  const { data: guides = [], isLoading, error } = useApi("guides", "/guides");
  const [q, setQ] = useState("");
  const [tour, setTour] = useState(null);
  if (error) return <ErrorState />;
  const filtered = guides.filter((g) => g.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader eyebrow="Navitra Hub" title="Help & Guides" subtitle="Step-by-step walkthroughs to get productive fast, plus a verified guide library — the admin never leaves Navitra." />

      <Tabs defaultValue="walkthroughs">
        <TabsList data-testid="help-tabs">
          <TabsTrigger value="walkthroughs" data-testid="tab-walkthroughs"><PlayCircle className="h-3.5 w-3.5 mr-1.5" />Walkthroughs</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-library"><LifeBuoy className="h-3.5 w-3.5 mr-1.5" />Guide library</TabsTrigger>
        </TabsList>

        <TabsContent value="walkthroughs" className="pt-4">
          <div className="rounded-lg border border-gold/30 bg-gold/[0.06] p-3 flex items-start gap-2 text-sm mb-4">
            <Sparkles className="h-4 w-4 text-gold mt-0.5 shrink-0" />
            <p className="text-muted-foreground"><span className="font-medium text-foreground">Guided tours.</span> Each walkthrough breaks a real workflow into a few clear steps — perfect for onboarding new admins in minutes.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TOURS.map((t) => {
              const Icon = t.icon;
              return (
                <Card key={t.id} className="flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`walkthrough-${t.id}`}>
                  <CardContent className="p-4 flex flex-col flex-1">
                    <div className="flex items-center gap-2.5 mb-2">
                      <span className="h-9 w-9 rounded-lg flex items-center justify-center shadow-sm shrink-0" style={{ background: t.color }}><Icon className="h-4.5 w-4.5 text-white" style={{ height: 18, width: 18 }} /></span>
                      <div className="min-w-0"><p className="font-display text-base leading-tight">{t.title}</p><p className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{t.steps.length} steps</p></div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-snug flex-1">{t.intro}</p>
                    <div className="flex flex-wrap gap-1 mt-3">{t.audience.map((a) => <Badge key={a} variant="outline" className="text-[10px]">{a}</Badge>)}</div>
                    <Button size="sm" className="mt-3 w-full" onClick={() => setTour(t)} data-testid={`start-${t.id}`}><PlayCircle className="h-4 w-4 mr-1.5" />Start walkthrough</Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="library" className="pt-4">
          <div className="relative mb-4 max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guides, runbooks, policy templates…" className="pl-9" data-testid="guide-search" /></div>
          {isLoading ? <LoadingBlock /> : (
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-2">
                <CardHeader><CardTitle className="font-display text-lg">Guide library</CardTitle></CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible>
                    {filtered.map((g) => (
                      <AccordionItem key={g.id} value={g.id} data-testid={`guide-${g.id}`}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex-1 flex items-center justify-between pr-3">
                            <span className="font-medium text-left">{g.title}</span>
                            <span className="flex gap-1.5">{g.roles.slice(0, 2).map((r) => <Badge key={r} variant="outline" className="text-[10px]">{r}</Badge>)}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-line">{g.body}</div>
                          <p className="text-xs text-muted-foreground mt-3 mono">Last verified {fmtDate(g.last_verified)} · vendor API {g.vendor_api_version}{g.connector ? ` · ${g.connector}` : ""}</p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
              <Card className="h-fit">
                <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><LifeBuoy className="h-4 w-4 text-gold" />Troubleshooter</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {[["CP06-401-KEY-SCOPE", "Compliance key missing scope", "Re-issue the Compliance Access Key in claude.ai org settings with full activity scope."], ["CP09-503-AUTH", "Azure OpenAI auth rejected", "Switch to managed identity from the Navitra Gate; disable key-based auth."], ["CP03-429-CREDIT", "Copilot Studio capacity throttled", "Raise credit capacity or reduce generative retrieval on the agent."]].map(([code, title, fix]) => (
                    <div key={code} className="rounded-lg border p-3"><p className="mono text-xs text-sev-danger">{code}</p><p className="font-medium mt-1">{title}</p><p className="text-muted-foreground text-xs mt-1">{fix}</p></div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <WalkthroughDialog tour={tour} onOpenChange={(o) => { if (!o) setTour(null); }} />
    </div>
  );
}
